import { getSymbol, Input, Select } from "@xola/ui-kit";
import { produce } from "immer";
import React from "react";
import { AdjustmentOperation, AdjustmentType, DemographicPrice, PricingGroup, PricingTerm } from "types";
import { useSeller } from "../../../hooks/useAccount";
import { useLocalizationStore } from "@/stores/localizationStore";

interface RelativePricingEditorProps {
    error: any;
    pricingGroup: PricingGroup;
    onChange: (terms: PricingTerm[]) => void;
}

const getRelativePriceFromPricingGroup = (pricingGroup: PricingGroup) => {
    // Get the price of the first item since all items will have same values
    return pricingGroup.terms?.[0].demographicPrices?.[0] ?? {};
};

export const RelativePricingEditor = ({ error, pricingGroup, onChange }: RelativePricingEditorProps) => {
    const seller = useSeller();
    const locale = useLocalizationStore().getLocale();

    const relativePrice = getRelativePriceFromPricingGroup(pricingGroup);

    const handleChange = (key: keyof DemographicPrice, value?: any) => {
        const updatedPricingGroup = produce(pricingGroup, (draft) => {
            const currentTerm = { ...draft.terms[0] };
            for (const demographicPrice of currentTerm.demographicPrices as any) {
                demographicPrice[key] = value;
            }

            draft.terms[0] = currentTerm;
        });

        onChange([updatedPricingGroup.terms[0]]);
    };

    return (
        <div className="space-y-4">
            <div className="inline-flex flex-grow-0">
                <Input
                    isRequired
                    className="h-9 w-25 rounded-r-none"
                    value={relativePrice.adjustmentAmount}
                    type="number"
                    title={error?.adjustmentAmount}
                    onChange={(e: any) =>
                        handleChange(`adjustmentAmount`, e.target.value ? Number(e.target.value) : undefined)
                    }
                />
                <Select
                    disabled={relativePrice.adjustmentOperation === AdjustmentOperation.FINAL}
                    className="w-20 rounded-none"
                    value={relativePrice.adjustmentType}
                    onChange={(e: any) => handleChange(`adjustmentType`, e.target.value)}
                >
                    <option value={AdjustmentType.ABSOLUTE}>{getSymbol(seller.currency, locale)}</option>
                    <option value={AdjustmentType.PERCENT}>%</option>
                </Select>
                <Select
                    className="mr-4 w-28 rounded-l-none"
                    value={relativePrice.adjustmentOperation}
                    onChange={(e: any) => handleChange(`adjustmentOperation`, e.target.value)}
                >
                    <option value={AdjustmentOperation.MARKUP}>Markup</option>
                    <option value={AdjustmentOperation.MARKDOWN}>Markdown</option>
                </Select>
            </div>
            <span className="text-gray-darker">of public pricing</span>
        </div>
    );
};
